/**
 * Sammelkonvertierung der Bestand-Texte über viele Punkte — äquivalent zum
 * Import: sicher Erkanntes wird in SEA-Form gebracht, Unsicheres bleibt Rohtext
 * (vom „Bestand"-Validator gelb markiert), der Originaltext bleibt im Freitext.
 * Reine Funktionen (ohne Angular) für leichte Testbarkeit.
 */
import { BestandTarget, convertOnImport, istBestandSeaKonform, parseBestand } from './bestand-parser';

/** Minimaler Punkt-Ausschnitt, den die Batch-Funktionen brauchen. */
export interface BestandBatchPoint {
	extendedHeader: BestandTarget;
}

export interface BestandBatchPlan {
	/** Punkte mit (nicht-leerem) Bestand-Text. */
	gesamt: number;
	/** schon SEA-konform — werden nicht angefasst. */
	konform: number;
	/** sicher konvertierbar (keine offenen Hinweise). */
	konvertierbar: number;
	/** bleiben zur manuellen Prüfung (unbekannte Codes/Prosa). */
	pruefung: number;
	/** nicht schreibbar (Workflow-Status/Rechte) — übersprungen. */
	keinRecht: number;
}

function bestandText(p: BestandBatchPoint): string | null {
	const ba = p.extendedHeader?.baumarten;
	return typeof ba === 'string' && ba.trim() ? ba.trim() : null;
}

/** Trockenlauf: zählt, was passieren würde — ohne etwas zu ändern. */
export function planBestandBatch<T extends BestandBatchPoint>(
	points: readonly T[], canWrite: (p: T) => boolean = () => true,
): BestandBatchPlan {
	const plan: BestandBatchPlan = { gesamt: 0, konform: 0, konvertierbar: 0, pruefung: 0, keinRecht: 0 };
	for (const p of points) {
		const text = bestandText(p);
		if (!text) continue;
		plan.gesamt++;
		if (istBestandSeaKonform(text)) { plan.konform++; continue; }
		if (!canWrite(p)) { plan.keinRecht++; continue; }
		const r = parseBestand(text);
		if (r.issues.length === 0 && (r.kind === 'ok' || r.kind === 'offenland')) plan.konvertierbar++;
		else plan.pruefung++;
	}
	return plan;
}

export interface BestandBatchRow {
	id: string;
	nummer: string;
	alt: string;
	neu: string;
}

/**
 * Trockenlauf für die Vorschau: liefert pro sicher konvertierbarem Punkt die
 * Zeile alt→neu (ohne zu mutieren). Schon konforme, unsichere und nicht
 * schreibbare Punkte erscheinen nicht.
 */
export function previewBestandBatch<T extends BestandBatchPoint & { _id?: string; header?: { nummer?: string } }>(
	points: readonly T[], canWrite: (p: T) => boolean = () => true,
): BestandBatchRow[] {
	const rows: BestandBatchRow[] = [];
	for (const p of points) {
		const text = bestandText(p);
		if (!text || istBestandSeaKonform(text) || !canWrite(p)) continue;
		const r = parseBestand(text);
		if (r.issues.length === 0 && (r.kind === 'ok' || r.kind === 'offenland')) {
			rows.push({ id: p._id ?? '', nummer: p.header?.nummer || (p._id ?? ''), alt: text, neu: r.kanonisch });
		}
	}
	return rows;
}

/**
 * Führt die Konvertierung aus (mutiert die Punkte) und gibt die geänderten
 * zurück (zum gebündelten Speichern via bulkDocs). Schon konforme und nicht
 * schreibbare Punkte bleiben unangetastet.
 */
export function runBestandBatch<T extends BestandBatchPoint>(
	points: readonly T[], canWrite: (p: T) => boolean = () => true,
): T[] {
	const changed: T[] = [];
	for (const p of points) {
		const text = bestandText(p);
		if (!text) continue;
		if (istBestandSeaKonform(text)) continue;
		if (!canWrite(p)) continue;
		convertOnImport(p.extendedHeader);
		changed.push(p);
	}
	return changed;
}
